import { PrimaryButton } from "@starrez/starryui";
import { Formik, FormikProps } from "formik";
import React from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { CommunityParticipantType, IParticipantEmailDataModel } from "../containers/EmailWizard/models/ParticipantEmail";
import { SendEmailExecuteModel } from "../containers/EmailWizard/models/SendEmail/SendEmailExecuteModel";
import { ParticipantSelector, ParticipantSelectType } from "../containers/EmailWizard/steps/ParticipantDetails/ParticipantSelector";
import { strings } from "../localisation";
import { ToggleButton } from "./Buttons/ToggleButton";

export const ComponentOverviewContainer = styled.div`
    font-family: 'Roboto';
    max-width: 600px;
    display: flex;
    flex-direction: column;
    margin: 40px auto;
`;

export const ComponentWrapper = styled.div`
    & + & {
        padding-top: 15px;
        margin-top: 40px;
        border-top: 1px solid rgba(0,0,0,0.2);
    }
    &:last-child {
        margin-bottom: 40px;
    }
`;

const TestSendParticipantEmailDataModel = new SendEmailExecuteModel<IParticipantEmailDataModel>([1], {
    TemplateID: -1,

    Attachments: [],
    AttachmentsCollection: [],

    CorrespondenceName: "Email Name",
    EmailTo: [],
    EmailToItems: [],

    EmailFromID: 0,
    EmailFromItem: null,

    EmailCC: false,
    EmailCCAddress: [],
    EmailCCAddressItems: [],
    EmailBCC: false,
    EmailBCCAddress: [],
    EmailBCCAddressItems: [],
    CCAddressOnlyFirst: true,
    BCCAddressOnlyFirst: true,
    EmailSubject: "Hey {NameFirst} {NameLast} - Advanced Email Wizard",
    EmailBody: "",

    UnlayerTemplate: {},

    CopyToCorrespondence: true,
    TemporaryAttachmentFolder: "_attachments_6840b501-5274-4522-8a71-ea80a6c87e85",
    ViewOnWeb: true,

    transformData() { },

    ParticipantEntryIDs: [],
    ParticipantSelectItems: [],

    CommunityParticipantType: CommunityParticipantType.Concern,
    ParticipantSelectorOptions: undefined,
});

export const ComponentOverview = () => {

    // Participant Selector //
    const participantSelectorSubmit = (values: SendEmailExecuteModel<IParticipantEmailDataModel>) => {
        let message = `Participant Selector Valid:\r\n\r\n${values.formDataModel.ParticipantSelectorOptions}`;
        if (values.formDataModel.ParticipantSelectorOptions === ParticipantSelectType.SelectedParticipants) {
            message += `\r\n\r\n${values.formDataModel.ParticipantEntryIDs.join("\r\n")}`;
        }
        alert(message);
    };
    const participantValidationSchema = Yup.object().shape({
        formDataModel: Yup.object({
            ParticipantSelectType: Yup.string().oneOf(Object.values(ParticipantSelectType)).required().label(strings.Label_ParticipantSelector_RecipientType),
            ParticipantSelectItems: Yup.array().nullable().when("ParticipantSelectType", {
                is: (value) => value === ParticipantSelectType.SelectedParticipants, then: () => {
                    return Yup.array().required(strings.Label_ParticipantSelector_SelectParticipantsInvalid).nullable();
                },
            }),
        }),
    });

    const components: { [name: string]: JSX.Element } = {

        "Participant Selector":
            <Formik
                onSubmit={participantSelectorSubmit}
                initialValues={TestSendParticipantEmailDataModel}
                validationSchema={participantValidationSchema}
            >
                {(formikProps: FormikProps<any>) => {
                    return (
                        <form>
                            <ParticipantSelector
                                FormikProps={formikProps}
                                Required={true}
                                FieldLabel={"Recipients"}
                                FieldGroup={"Standard"}
                                Participants={[
                                    { participantID: "0", name: "Michael Scott" },
                                    { participantID: "1", name: "Jim Halpert" },
                                    { participantID: "2", name: "Creed Bratton" },
                                    { participantID: "3", name: "Ryan Howard" },
                                ]}
                            />

                            <PrimaryButton
                                Caption={"Submit"}
                                Type={"button"}
                                OnClick={formikProps.submitForm}
                            />
                        </form>
                    );
                }}
            </Formik>,

        "Participant Selector - Overridden options":
            <Formik
                onSubmit={participantSelectorSubmit}
                initialValues={TestSendParticipantEmailDataModel}
                validationSchema={participantValidationSchema}
            >
                {(formikProps: FormikProps<any>) => {
                    return (
                        <form>
                            <ParticipantSelector
                                FormikProps={formikProps}
                                Required={true}
                                FieldLabel={"Recipients"}
                                FieldGroup={"Overridden"}
                                SelectionTypeOptions={{
                                    [ParticipantSelectType.All]: "Send To All Involved",
                                    [ParticipantSelectType.SelectedParticipants]: "Pick some peeps (Custom Text)",
                                }}
                                Participants={[
                                    { participantID: "0", name: "Michael Scott" },
                                    { participantID: "1", name: "Jim Halpert" },
                                    { participantID: "2", name: "Creed Bratton" },
                                    { participantID: "3", name: "Ryan Howard" },
                                ]}
                            />

                            <PrimaryButton
                                Caption={"Submit"}
                                Type={"button"}
                                OnClick={formikProps.submitForm}
                            />
                        </form>
                    );
                }}
            </Formik>,

        "Participant Selector - Only one Option":
            <Formik
                onSubmit={participantSelectorSubmit}
                initialValues={{
                    ...TestSendParticipantEmailDataModel,
                    formDataModel: {
                        ...TestSendParticipantEmailDataModel.formDataModel,
                        ParticipantSelectType: ParticipantSelectType.All,
                    },
                }}
                validationSchema={participantValidationSchema}
            >
                {(formikProps: FormikProps<any>) => {
                    return (
                        <form>
                            <ParticipantSelector
                                FormikProps={formikProps}
                                Required={true}
                                FieldLabel={"Recipients"}
                                FieldGroup={"Overridden"}
                                SelectionTypeOptions={{
                                    [ParticipantSelectType.All]: "SendToAll",
                                }}
                                Participants={[
                                    { participantID: "0", name: "Michael Scott" },
                                    { participantID: "1", name: "Jim Halpert" },
                                    { participantID: "2", name: "Creed Bratton" },
                                    { participantID: "3", name: "Ryan Howard" },
                                ]}
                            />

                            <PrimaryButton
                                Caption={"Submit"}
                                Type={"button"}
                                OnClick={formikProps.submitForm}
                            />
                        </form>
                    );
                }}
            </Formik>,

        "Toggle Button":
            <ToggleButton
                CheckedCaption={"Checked"}
                UncheckedCaption={"Unchecked"}
                Toggled={false}
                OnClick={(checked: boolean) => alert(`Toggle Button Clicked - ${checked}`)}
            />,
    };

    return (
        <ComponentOverviewContainer>
            {Object.keys(components).map((name) => (
                <ComponentWrapper key={name}>
                    <h1>
                        {name}
                    </h1>

                    <div>
                        {components[name]}
                    </div>
                </ComponentWrapper>
            ))}
        </ComponentOverviewContainer>
    );
};
